import React, { useState } from 'react'
import Layout from '../layout/Layout'
import SEO from '../components/SEO'

const contactEndpoint = process.env.REACT_APP_CONTACT_ENDPOINT || 'https://notify.paprejatechsolutions.com/api/contact'

const initialForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  consent: false,
}

function ContactIcon({ type }) {
  const props = {
    className: 'h-5 w-5',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }

  if (type === 'email') return <svg {...props}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
  if (type === 'phone') return <svg {...props}><path d="M6.5 3.5h3l1.5 4-2 1.5a15 15 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2 2C10.5 19.5 4.5 13.5 4.5 6a2 2 0 0 1 2-2.5Z" /></svg>
  if (type === 'clock') return <svg {...props}><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></svg>
  if (type === 'user') return <svg {...props}><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>
  if (type === 'subject') return <svg {...props}><path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" /></svg>
  if (type === 'message') return <svg {...props}><path d="M5 5h14v10H9l-4 4V5Z" /><path d="M8 9h8M8 12h5" /></svg>
  return <svg {...props}><path d="M12 3 4.5 6v5.5c0 4.5 3 7.8 7.5 9.5 4.5-1.7 7.5-5 7.5-9.5V6L12 3Z" /></svg>
}

function Contact() {
  const [form, setForm] = useState(initialForm)
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target
    setForm((currentForm) => ({ ...currentForm, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitting(true)
    setStatus({ type: '', message: '' })

    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          mobile: form.phone,
          service: form.subject,
          message: form.message,
        }),
        signal: controller.signal,
      })
      window.clearTimeout(timeout)
      if (!response.ok) {
        throw new Error('Unable to send your message.')
      }

      setForm(initialForm)
      setStatus({ type: 'success', message: 'Thanks for reaching out. We will get back to you soon.' })
    } catch (error) {
      setStatus({ type: 'error', message: error.name === 'AbortError' ? 'The request took too long. Please try again.' : error.message || 'Something went wrong. Please try again.' })
    } finally {
      window.clearTimeout(timeout)
      setIsSubmitting(false)
    }
  }

  return (
    <Layout>
      <SEO
        title="Contact Papreja Tech Solutions | Start a Conversation"
        description="Talk to Papreja Tech Solutions about practical technology, design, and engineering for your next business move."
        path="/contact"
      />
      <main className="overflow-hidden bg-slate-50 dark:bg-slate-950">
        <section className="relative bg-[#071a3d] text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-[#071a3d] via-[#0b2d63] to-[#123f7a]" />
          <div className="relative mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:px-10">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-300">Get in touch</p>
            <h1 className="mt-4 max-w-2xl text-4xl font-black tracking-tight sm:text-5xl">Let&apos;s build something useful.</h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-blue-100">Tell us what you are working on, what is getting in the way, or where you want to go next.</p>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-[0.8fr_1.2fr] lg:px-10">
          <aside className="rounded-2xl bg-[#071a3d] p-7 text-white shadow-xl shadow-blue-950/10 sm:p-9">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-400 text-xl font-black text-[#071a3d]">P</span>
            <h2 className="mt-8 text-2xl font-black">We&apos;re here to help.</h2>
            <p className="mt-3 text-sm leading-6 text-blue-100">A good conversation is often the first useful step. Reach out and we&apos;ll respond with clarity and care.</p>

            <div className="mt-10 space-y-6">
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-400 text-[#071a3d]"><ContactIcon type="email" /></span>
                <div><p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">Email</p>
                <a href="mailto:info@paprejatechsolutions.com" className="mt-2 block text-sm text-blue-100 transition hover:text-white">info@paprejatechsolutions.com</a>
                </div></div>
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-400 text-[#071a3d]"><ContactIcon type="phone" /></span>
                <div><p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">Phone</p>
                <a href="tel:+91-9716234724" className="mt-2 block text-sm text-blue-100 transition hover:text-white">+91 9716234724</a>
                </div></div>
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-400 text-[#071a3d]"><ContactIcon type="clock" /></span>
                <div><p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">Working hours</p>
                <p className="mt-2 text-sm text-blue-100">Mon-Sun: 9:00 AM - 6:00 PM 24/7 Support</p>
                </div></div>
            </div>
          </aside>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-9 dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-8">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Send a message</p>
              <h2 className="mt-3 text-2xl font-black text-slate-900 dark:text-white">How can we help?</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-2"><ContactIcon type="user" /> Name <span className="text-cyan-600">*</span></span>
                  <input name="name" value={form.name} onChange={handleChange} required autoComplete="name" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white" placeholder="Your name" />
                </label>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-2"><ContactIcon type="email" /> Email <span className="text-cyan-600">*</span></span>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required autoComplete="email" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white" placeholder="you@example.com" />
                </label>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-2"><ContactIcon type="phone" /> Phone</span>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} autoComplete="tel" className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white" placeholder="+1 (000) 000-0000" />
                </label>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-2"><ContactIcon type="subject" /> Subject <span className="text-cyan-600">*</span></span>
                  <input name="subject" value={form.subject} onChange={handleChange} required className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white" placeholder="What can we help with?" />
                </label>
              </div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                <span className="flex items-center gap-2"><ContactIcon type="message" /> Message <span className="text-cyan-600">*</span></span>
                <textarea name="message" value={form.message} onChange={handleChange} required rows="5" className="mt-2 w-full resize-y rounded-lg border border-slate-300 bg-white px-4 py-3 font-normal outline-none transition placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white" placeholder="Tell us a little about your project..." />
              </label>

              <label htmlFor="consent" className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                <input id="consent" name="consent" type="checkbox" checked={form.consent} onChange={handleChange} required className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-cyan-500 focus:ring-2 focus:ring-cyan-500/30 dark:border-slate-700 dark:bg-slate-950" />
                <span>I agree to be contacted about my enquiry and understand that my details will be used to respond.</span>
              </label>

              {status.message && (
                <p role="status" className={`rounded-lg px-4 py-3 text-sm ${status.type === 'success' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'}`}>
                  {status.message}
                </p>
              )}

              <button type="submit" disabled={isSubmitting} className="inline-flex rounded-lg bg-cyan-500 px-6 py-3 text-sm font-bold text-[#071a3d] transition hover:-translate-y-0.5 hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">
                {!isSubmitting && <ContactIcon type="message" />}
                {isSubmitting ? 'Sending...' : 'Send message'}
                {!isSubmitting && <span className="ml-2" aria-hidden="true">-&gt;</span>}
              </button>
            </form>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default Contact
